import { TeamMember } from '../../model/teammember/TeamMember';
import i18n from "../../i18n";
import { TeamMemberInput } from './TeamMemberInput';
import { CommissionConfig, CommissionConfigInput } from '../../model/teammember/Commission';

const API_URL = process.env.REACT_APP_BUSINESS_SERVICE_BASE_URL || '';

const COMMISSION_CONFIG_FIELDS = `
    teamMemberId
    defaultRate
    overrides { serviceId rate }
`;

const TEAM_MEMBER_FIELDS = `
    id
    firstName
    lastName
    publicName
    email
    phone
    color
    businessId
    allowOnlineBooking
    hasAppAccess
    isAdmin
    active
    roleId
    roleBusinessId
    serviceIds
    workingHours {
        id
        dayOfWeek
        startTime
        endTime
        allowBookingOnIntervalTime
        startIntervalTime
        endIntervalTime
        overnightEnd
    }
`;

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

/** Scoped lookup — only this business's team members. The global
 *  getAllTeamMembers was removed (leaked every business's people). */
export async function getTeamMembersByBusinessId(
    businessId: number | string,
    token: string
): Promise<TeamMember[]> {
    const data = await gqlPost<{ getTeamMembersByBusinessId: TeamMember[] | null }>(
        token,
        `query getTeamMembersByBusinessId($businessId: ID!) {
            getTeamMembersByBusinessId(businessId: $businessId) { ${TEAM_MEMBER_FIELDS} }
        }`,
        { businessId }
    );
    return data.getTeamMembersByBusinessId ?? [];
}

export async function saveTeamMember(input: TeamMemberInput, token: string): Promise<TeamMember> {
    const data = await gqlPost<{ saveTeamMember: TeamMember }>(
        token,
        `mutation saveTeamMember($input: TeamMemberInput!) {
            saveTeamMember(input: $input) { ${TEAM_MEMBER_FIELDS} }
        }`,
        { input }
    );
    return data.saveTeamMember;
}

export async function setTeamMemberActive(
    id: string,
    active: boolean,
    token: string,
): Promise<TeamMember> {
    const data = await gqlPost<{ setTeamMemberActive: TeamMember }>(
        token,
        `mutation setTeamMemberActive($id: ID!, $active: Boolean!) {
            setTeamMemberActive(id: $id, active: $active) { ${TEAM_MEMBER_FIELDS} }
        }`,
        { id, active },
    );
    return data.setTeamMemberActive;
}

export async function deleteTeamMember(id: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ deleteTeamMember: boolean }>(
        token,
        `mutation deleteTeamMember($id: ID!) { deleteTeamMember(id: $id) }`,
        { id }
    );
    return !!data.deleteTeamMember;
}

// --- Commission config (default rate + per-service overrides). COMMISSIONS gated. ---

export async function getTeamMemberCommission(
    businessId: string,
    teamMemberId: string,
    token: string
): Promise<CommissionConfig | null> {
    const data = await gqlPost<{ getTeamMemberCommission: CommissionConfig | null }>(
        token,
        `query getTeamMemberCommission($businessId: ID!, $teamMemberId: ID!) {
            getTeamMemberCommission(businessId: $businessId, teamMemberId: $teamMemberId) { ${COMMISSION_CONFIG_FIELDS} }
        }`,
        { businessId, teamMemberId }
    );
    return data.getTeamMemberCommission ?? null;
}

export async function saveTeamMemberCommission(
    input: CommissionConfigInput,
    token: string
): Promise<CommissionConfig> {
    const data = await gqlPost<{ saveTeamMemberCommission: CommissionConfig }>(
        token,
        `mutation saveTeamMemberCommission($input: CommissionConfigInput!) {
            saveTeamMemberCommission(input: $input) { ${COMMISSION_CONFIG_FIELDS} }
        }`,
        { input }
    );
    return data.saveTeamMemberCommission;
}
